import './InternalSecondaryMobile.css';
// src/MaintenanceCheckIns.js
//
// Daily Technician Check In page (one day at a time).
//
// Lives under the internal Maintenance hub at /maintenance/check-ins.
// Renders a single table — one row per technician who logged activity
// on the selected date — joining the per-tech daily analytics from
// /api/getTechStartTimes (using start_date == end_date) with the live
// uniform-selfie archive from /api/field/daily-check-ins.
//
// Defaults to today (Toronto local). The "Day" date picker is the
// only filter; everything else (lateness threshold, target time, etc.)
// matches MaintenanceAnalytics so the badges read the same.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import API_BASE_URL from './config/api';

const TARGET_MIN = 9 * 60 + 15; // 9:15 AM target for first upload

// ---- shared utilities ----
// "Service day" rolls over at 6 AM local time so that late-night
// submissions (e.g. backfilled units uploaded at 12:11 AM) count toward
// the day the tech was actually on site. The dashboard's "Today" button
// follows the same convention so it aligns with both the uniform check
// gate and the per-tech first/last-upload analytics.
const SERVICE_DAY_ROLLOVER_HOUR = 6;
const todayISO = () => {
  const d = new Date();
  if (d.getHours() < SERVICE_DAY_ROLLOVER_HOUR) {
    d.setDate(d.getDate() - 1);
  }
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

function badgeForFirst(mins, isOutlier) {
  if (mins == null) return { cls: 'muted', label: '—' };
  if (isOutlier) return { cls: 'muted', label: 'Outlier' };
  if (mins <= TARGET_MIN) return { cls: 'green', label: 'On time' };
  if (mins <= TARGET_MIN + 15) return { cls: 'yellow', label: 'Slightly late' };
  if (mins <= TARGET_MIN + 30) return { cls: 'orange', label: 'Late' };
  return { cls: 'red', label: 'Very late' };
}

// ---- color tokens (matching MaintenanceAnalytics) ----
const C = {
  navy: '#1F3A68',
  navySoft: '#2C4E84',
  ink: '#1a2236',
  inkSoft: '#4a5468',
  muted: '#7a8499',
  line: '#E6E8EC',
  bg: '#F4F7FB',
  card: '#ffffff',
  green: '#1c9550',
  greenSoft: '#e3f5ea',
  yellow: '#C68A1C',
  yellowSoft: '#fdf3df',
  orange: '#C76121',
  orangeSoft: '#fcecdd',
  red: '#B62525',
  redSoft: '#fbe2e2',
  shadow: '0 1px 3px rgba(20, 30, 60, .06), 0 8px 24px rgba(20, 30, 60, .04)',
};

const sharedFont = '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif';

function Badge({ cls, children }) {
  const map = {
    green: { bg: C.greenSoft, fg: C.green },
    yellow: { bg: C.yellowSoft, fg: C.yellow },
    orange: { bg: C.orangeSoft, fg: C.orange },
    red: { bg: C.redSoft, fg: C.red },
    muted: { bg: '#eef1f6', fg: C.muted },
  };
  const m = map[cls] || map.muted;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 9px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        background: m.bg,
        color: m.fg,
      }}
    >
      {children}
    </span>
  );
}

export default function MaintenanceCheckIns() {
  const [date, setDate] = useState(() => todayISO());
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [checkIns, setCheckIns] = useState([]);
  const [checkInsLoading, setCheckInsLoading] = useState(false);

  // Fetch per-(tech, day) analytics for just this day. We reuse the
  // existing getTechStartTimes endpoint with start_date == end_date so
  // we don't add yet another aggregation surface.
  const fetchAnalytics = useCallback(async (d) => {
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      const params = new URLSearchParams({
        exclude_outliers: 'true',
        start_date: d,
        end_date: d,
      });
      const res = await fetch(`${API_BASE_URL}/api/getTechStartTimes?${params}`, {
        credentials: 'omit',
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`HTTP ${res.status}: ${txt.slice(0, 200)}`);
      }
      const json = await res.json();
      setAnalytics(json);
    } catch (e) {
      console.error('[MaintenanceCheckIns] analytics fetch failed', e);
      setAnalyticsError(e.message || 'Failed to load data');
      setAnalytics(null);
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  const fetchCheckIns = useCallback(async (d) => {
    setCheckInsLoading(true);
    try {
      const params = new URLSearchParams({ start_date: d, end_date: d });
      const res = await fetch(`${API_BASE_URL}/api/field/daily-check-ins?${params}`, {
        credentials: 'omit',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setCheckIns(Array.isArray(json.checkIns) ? json.checkIns : []);
    } catch (e) {
      console.warn('[MaintenanceCheckIns] check-ins fetch failed', e);
      setCheckIns([]);
    } finally {
      setCheckInsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!date) return;
    fetchAnalytics(date);
    fetchCheckIns(date);
  }, [date, fetchAnalytics, fetchCheckIns]);

  const techDays = useMemo(() => analytics?.tech_days || [], [analytics]);

  const checkInsByKey = useMemo(() => {
    const m = {};
    for (const c of checkIns) {
      const k = `${(c.technician || '').trim().toLowerCase()}|${c.date}`;
      if (!m[k]) m[k] = c;
    }
    return m;
  }, [checkIns]);

  const rows = useMemo(() => {
    return techDays.map((td) => {
      const key = `${(td.technician || '').trim().toLowerCase()}|${td.date}`;
      return { ...td, checkIn: checkInsByKey[key] || null };
    });
  }, [techDays, checkInsByKey]);

  const verifiedCount = rows.filter((r) => r.checkIn && r.checkIn.photoUrl).length;

  const fmtStamp = (epoch) => {
    if (!epoch) return '—';
    try {
      const d = new Date(epoch * 1000);
      let h = d.getHours();
      const m = String(d.getMinutes()).padStart(2, '0');
      const period = h < 12 ? 'AM' : 'PM';
      h = h % 12 || 12;
      return `${h}:${m} ${period}`;
    } catch { return '—'; }
  };

  const dateLabel = (() => {
    try {
      const d = new Date(`${date}T12:00:00`);
      return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    } catch { return date; }
  })();

  const renderTable = () => {
    if (analyticsLoading && !analytics) {
      return <div style={emptyStyle}><span style={spinnerStyle} /> Loading…</div>;
    }
    if (analyticsError) {
      return <div style={{ ...emptyStyle, color: C.red }}>Couldn't load data: {analyticsError}</div>;
    }
    if (!rows.length) {
      return <div style={emptyStyle}>No technician activity logged on {dateLabel}.</div>;
    }
    return (
      <div className="internal-data-scroll" role="region" aria-label="Scrollable data table" tabIndex={0} style={{ overflowX: 'auto' }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Technician</th>
              <th style={{ ...thStyle, ...colBuildingStyle }}>Building</th>
              <th style={{ ...thStyle, ...thNumStyle }}>Units</th>
              <th style={thStyle}>Uniform Verification</th>
              <th style={thStyle}>First Upload</th>
              <th style={thStyle}>Final Upload</th>
              <th style={{ ...thStyle, ...thNumStyle }}>Avg Gap</th>
              <th style={thStyle}>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d, i) => {
              const b = badgeForFirst(d.first_minutes, d.is_first_outlier);
              const ci = d.checkIn;
              const bldgs = (d.buildings_on_day && d.buildings_on_day.length > 1)
                ? d.buildings_on_day.join(', ')
                : (d.building_name || '—');
              return (
                <tr key={`${d.date}-${d.technician}-${i}`} style={trStyle}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600, color: C.ink }}>{d.technician || '—'}</div>
                  </td>
                  <td style={{ ...tdStyle, ...colBuildingStyle }} title={bldgs}>{bldgs}</td>
                  <td style={{ ...tdStyle, ...tdNumStyle }}>{d.upload_count}</td>
                  <td style={tdStyle}>
                    {ci && ci.photoUrl ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <a
                          href={ci.photoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={`Verification photo captured at ${fmtStamp(ci.createdAtEpoch)}`}
                        >
                          <img
                            src={ci.photoUrl}
                            alt={`${d.technician} uniform verification`}
                            style={checkInThumbStyle}
                            loading="lazy"
                          />
                        </a>
                        <span style={{ color: C.muted, fontSize: 11 }}>
                          {fmtStamp(ci.createdAtEpoch)}
                        </span>
                      </div>
                    ) : (
                      <Badge cls="red">Missing</Badge>
                    )}
                  </td>
                  <td style={{ ...tdStyle, ...colTimeStyle }}>{d.first_time}</td>
                  <td style={{ ...tdStyle, ...colTimeStyle }}>{d.last_time}</td>
                  <td style={{ ...tdStyle, ...tdNumStyle }}>{d.avg_gap_display || '—'}</td>
                  <td style={tdStyle}><Badge cls={b.cls}>{b.label}</Badge></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div style={{ background: C.bg, color: C.ink, fontFamily: sharedFont, fontSize: 14, minHeight: '100vh' }}>
      <div className="internal-secondary-page internal-checkins-page" style={{ maxWidth: 1320, margin: '0 auto', padding: '28px 24px 64px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ fontSize: 26, margin: '0 0 4px 0', color: C.navy, fontWeight: 700, letterSpacing: '-0.01em' }}>
              Daily Technician Check In
            </h1>
            <div style={{ color: C.muted, fontSize: 13 }}>
              Live uniform &amp; name tag verification photos for every tech who worked on the selected day.{' '}
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, background: '#eef3fb', color: C.navy, fontWeight: 600, fontSize: 12 }}>
                Target: first upload ≤ 9:15 AM
              </span>
            </div>
          </div>
          {analytics && (
            <div style={{ fontSize: 12, color: C.muted, textAlign: 'right' }}>
              <div>Generated {analytics.generated_at}</div>
            </div>
          )}
        </div>

        <div style={filterCardStyle}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={labelStyle} htmlFor="check-in-date">Day</label>
            <input
              id="check-in-date"
              type="date"
              value={date}
              max={todayISO()}
              onChange={(e) => setDate(e.target.value || todayISO())}
              style={dateInputStyle}
            />
          </div>
          <button type="button" style={resetBtnStyle} onClick={() => setDate(todayISO())}>
            Today
          </button>
          <div style={{ flex: '1 1 auto' }} />
          <div style={{ fontSize: 13, color: C.inkSoft, fontWeight: 600 }}>
            {dateLabel}
          </div>
        </div>

        <div style={tableCardStyle}>
          <div className="checkins-table-header" style={tableHeadStyle}>
            <h3 style={{ margin: 0, fontSize: 14, color: C.ink, fontWeight: 700 }}>
              {dateLabel}
            </h3>
            <div style={{ color: C.muted, fontSize: 12 }}>
              {checkInsLoading
                ? 'Loading verification photos…'
                : rows.length === 0
                  ? 'No technicians worked this day'
                  : `${rows.length} tech${rows.length === 1 ? '' : 's'} · ${verifiedCount} verified`}
            </div>
          </div>
          {renderTable()}
        </div>
      </div>
    </div>
  );
}

// ----- styles -----
const filterCardStyle = {
  background: C.card,
  border: `1px solid ${C.line}`,
  borderRadius: 12,
  padding: '14px 16px',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '14px 18px',
  alignItems: 'flex-end',
  boxShadow: C.shadow,
  marginBottom: 18,
};

const labelStyle = {
  fontSize: 11,
  textTransform: 'uppercase',
  color: C.muted,
  fontWeight: 600,
  letterSpacing: '.05em',
};

const dateInputStyle = {
  appearance: 'none',
  WebkitAppearance: 'none',
  background: C.card,
  border: `1px solid ${C.line}`,
  borderRadius: 8,
  padding: '7px 10px',
  fontSize: 14,
  color: C.ink,
  minWidth: 160,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const resetBtnStyle = {
  fontSize: 12,
  padding: '6px 12px',
  border: `1px solid ${C.line}`,
  borderRadius: 8,
  background: C.card,
  color: C.inkSoft,
  cursor: 'pointer',
};

const tableCardStyle = {
  background: C.card,
  border: `1px solid ${C.line}`,
  borderRadius: 14,
  boxShadow: C.shadow,
  overflow: 'hidden',
  marginBottom: 22,
};
const tableHeadStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '14px 18px',
  borderBottom: `1px solid ${C.line}`,
  gap: 12,
  flexWrap: 'wrap',
};
const tableStyle = { width: '100%', borderCollapse: 'collapse' };
const thStyle = {
  textAlign: 'left',
  padding: '11px 14px',
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  color: C.muted,
  background: '#fafbfd',
  borderBottom: `1px solid ${C.line}`,
  letterSpacing: '.04em',
  whiteSpace: 'nowrap',
};
const thNumStyle = { textAlign: 'right' };
const tdStyle = {
  padding: '12px 14px',
  fontSize: 13,
  color: C.ink,
  borderBottom: `1px solid ${C.line}`,
  verticalAlign: 'middle',
};
const tdNumStyle = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
const trStyle = {};
const colBuildingStyle = { maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const colTimeStyle = { fontVariantNumeric: 'tabular-nums' };
const checkInThumbStyle = {
  width: 44,
  height: 44,
  borderRadius: 10,
  objectFit: 'cover',
  border: `1px solid ${C.line}`,
  display: 'block',
  cursor: 'zoom-in',
};

const emptyStyle = {
  textAlign: 'center',
  padding: '50px 16px',
  color: C.muted,
  fontSize: 14,
};
const spinnerStyle = {
  width: 22,
  height: 22,
  border: '2px solid #e1e6f0',
  borderTopColor: C.navy,
  borderRadius: '50%',
  display: 'inline-block',
  animation: 'spin 0.8s linear infinite',
  marginRight: 6,
  verticalAlign: 'middle',
};
